import React, { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  Paper,
  Button,
  TextField,
  CircularProgress,
  Divider,
  IconButton,
  Card as MuiCard,
  CardContent,
  Avatar,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import {
  CreditCard as CreditCardIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from "@material-ui/icons";
import withMiddleware from "../../../middleware";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { AddressElement } from "@stripe/react-stripe-js";
import {
  getPaymentMethods,
  removePaymentMethod,
  attachPaymentMethod,
  getOrgDetails,
  updateOrgBillingEmail,
} from "./billingSettingsHandler";
import { useUtils } from "../../../utils";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
const appearance = {
  theme: "stripe",

  variables: {
    colorPrimary: "#0570de",
    colorBackground: "#ffffff",
    colorText: "#30313d",
    colorDanger: "#df1b41",
    fontFamily: "Ideal Sans, system-ui, sans-serif",
    spacingUnit: "2px",
    borderRadius: "4px",
  },
};

function BillingPage({ service }) {
  return (
    <Elements stripe={stripePromise} options={{ appearance }}>
      <Grid
        container
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,

          paddingLeft: 114,
          paddingTop: 80,
          overflow: "auto",
        }}
      >
        {/* Title - Top Left */}
        <Grid item xs={12} style={{ alignSelf: "flex-start" }}>
          <Typography
            variant="h4"
            style={{ textAlign: "left", paddingLeft: 50 }}
          >
            Billing & Payments
          </Typography>
        </Grid>

        {/* Content Area - Top Aligned */}
        <Grid
          container
          item
          xs={12}
          spacing={4}
          style={{
            alignItems: "flex-start",
            justifyContent: "center",
          }}
        >
          {/* Payment Methods Section */}
          <Grid item xs={12} md={6} style={{ alignSelf: "flex-start" }}>
            <Paper style={{ padding: 24, height: "100%" }}>
              <PaymentMethods service={service} />
            </Paper>
          </Grid>

          {/* Billing Settings Section */}
          <Grid item xs={12} md={5} style={{ alignSelf: "flex-start" }}>
            <Paper style={{ padding: 24, height: "100%" }}>
              <BillingSettingsForm service={service} />
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </Elements>
  );
}

export default withMiddleware(BillingPage);

const BillingSettingsForm = ({ service }) => {
  const { showToast } = useUtils();
  const [formData, setFormData] = useState({
    billingEmail: "",
    orgName: "",
    billingStartDate: "N/A",
  });

  const [originalData, setOriginalData] = useState({});
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchOrgDetails = async () => {
      try {
        const orgDetails = await getOrgDetails(service);
        const data = {
          billingEmail: orgDetails.billing_email,
          orgName: orgDetails.name,
          billingStartDate: orgDetails.previous_billing_date || "N/A",
        };
        setFormData(data);
        setOriginalData(data);
      } catch (error) {
        console.error("Error fetching org details:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrgDetails();
  }, [service]);

  const validate = () => {
    const newErrors = {};

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.billingEmail)) {
      newErrors.billingEmail = "Please enter a valid email address";
    }

    if (formData.orgName.trim().length < 3) {
      newErrors.orgName = "Organization name must be at least 3 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const hasChanges = () => {
    return (
      formData.billingEmail !== originalData.billingEmail ||
      formData.orgName !== originalData.orgName
    );
  };

  const handleSave = async () => {
    if (!validate() || !hasChanges()) return;

    setIsSaving(true);
    try {
      await updateOrgBillingEmail(service, formData.billingEmail);
      setOriginalData(formData);
      showToast("success", "Billing details updated successfully.");
    } catch (error) {
      console.error("Error updating billing settings:", error);
      showToast("error", error.message || "Failed to update billing settings.");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Grid container justifyContent="center" style={{ padding: 24 }}>
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <>
      <Typography variant="h6">Billing Settings</Typography>
      <Divider style={{ margin: "16px 0" }} />

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            name="billingEmail"
            label="Billing Email"
            fullWidth
            value={formData.billingEmail}
            onChange={handleChange}
            error={!!errors.billingEmail}
            helperText={errors.billingEmail}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            name="orgName"
            label="Organization Name"
            fullWidth
            value={formData.orgName}
            onChange={handleChange}
            error={!!errors.orgName}
            helperText={errors.orgName}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Billing Start Date"
            fullWidth
            value={formData.billingStartDate}
            disabled
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={isSaving || !hasChanges()}
          >
            {isSaving ? <CircularProgress size={24} /> : "Save Changes"}
          </Button>
        </Grid>
      </Grid>
    </>
  );
};

const AddCardForm = ({ service, onSuccess, onCancel, setAsDefault }) => {
  const { showToast } = useUtils();
  const stripe = useStripe();
  const elements = useElements();
  const [makeDefault, setMakeDefault] = useState(setAsDefault);
  const [processing, setProcessing] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [addressComplete, setAddressComplete] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || !cardComplete || !addressComplete) return;

    setProcessing(true);

    try {
      const addressElement = elements.getElement(AddressElement);
      const addressValue = await addressElement?.getValue();

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardElement),
        billing_details: {
          address: addressValue?.value?.address || {},
        },
      });

      if (error) {
        console.error("[Stripe Error]", error);
        showToast("error", error.message || "Card creation failed.");
        return;
      }
      debugger;
      onSuccess({
        id: paymentMethod.id,
        card: paymentMethod.card,
        makeDefault,
      });
    } catch (err) {
      console.error("Error adding card:", err);
      showToast("error", err.message || "Failed to add card.");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Paper style={{ padding: 16, marginTop: 16 }}>
      <Typography variant="h6" gutterBottom>
        Add New Card
      </Typography>

      <form onSubmit={handleSubmit}>
        {/* Card Element */}
        <div
          style={{
            border: "1px solid #CBD5E0",
            borderRadius: "4px",
            padding: "12px",
            backgroundColor: "#fff",
            marginBottom: 16,
          }}
        >
          <CardElement
            options={{
              hidePostalCode: true,
              style: {
                base: {
                  color: "#30313d",
                  fontSize: "16px",
                  fontSmoothing: "antialiased",
                  fontFamily: "'Ideal Sans', system-ui, sans-serif",
                  "::placeholder": { color: "#a0aec0" },
                },
                invalid: { color: "#df1b41", iconColor: "#df1b41" },
              },
            }}
            onChange={(event) => {
              setCardComplete(event.complete);
            }}
          />
        </div>

        {/* Address Element */}
        <div
          style={{
            border: "1px solid #CBD5E0",
            borderRadius: "4px",
            padding: "12px",
            backgroundColor: "#fff",
            marginBottom: 16,
          }}
        >
          <AddressElement
            options={{
              mode: "billing",
              display: {
                name: "full",
                phone: "never",
              },
              fields: {
                name: "never",
                phone: "never",
              },
            }}
            onChange={(event) => {
              setAddressComplete(event.complete);
            }}
          />
        </div>

        {/* Default Payment Method Checkbox */}
        {!setAsDefault && (
          <div style={{ marginBottom: 16 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={makeDefault}
                  onChange={(e) => setMakeDefault(e.target.checked)}
                  color="primary"
                />
              }
              label="Set as default payment method"
            />
          </div>
        )}

        {/* Form Actions */}
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            onClick={onCancel}
            disabled={processing}
            style={{ marginRight: 8 }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={
              !stripe || processing || !cardComplete || !addressComplete
            }
          >
            {processing ? <CircularProgress size={24} /> : "Save Card"}
          </Button>
        </div>
      </form>
    </Paper>
  );
};

const PaymentMethods = ({ service }) => {
  const { showToast } = useUtils();
  const [cards, setCards] = useState([]);
  const [defaultCardId, setDefaultCardId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingCard, setAddingCard] = useState(false);

  useEffect(() => {
    async function loadPaymentMethods() {
      try {
        const { paymentMethods, defaultPaymentMethodId } =
          await getPaymentMethods(service);
        setCards(paymentMethods);
        setDefaultCardId(defaultPaymentMethodId);

        if (paymentMethods.length === 0) {
          console.log("info", "No payment methods found");
        }
      } catch (error) {
        console.error("Failed to load payment methods", error);
      } finally {
        setLoading(false);
      }
    }
    loadPaymentMethods();
  }, [service]);

  const handleSetDefault = async (cardId, isCurrentlyDefault) => {
    try {
      await attachPaymentMethod(service, {
        paymentMethodId: cardId,
        setAsDefault: !isCurrentlyDefault,
      });

      if (isCurrentlyDefault) {
        setDefaultCardId(null);
        setCards((prevCards) =>
          prevCards.map((card) => ({
            ...card,
            isDefault: false,
          }))
        );
      } else {
        setDefaultCardId(cardId);
        setCards((prevCards) =>
          prevCards.map((card) => ({
            ...card,
            isDefault: card.id === cardId,
          }))
        );
      }
    } catch (error) {
      console.error("Failed to update default payment method", error);
      showToast("error", error.message || "Failed to update default method.");
    }
  };

  const handleRemoveCard = async (cardId) => {
    try {
      await removePaymentMethod(service, cardId);
      setCards(cards.filter((card) => card.id !== cardId));

      if (cardId === defaultCardId) setDefaultCardId(null);
      showToast("success", "Payment method removed.");
    } catch (error) {
      console.error("Failed to remove payment method", error);
      showToast("error", error.message || "Failed to remove payment method.");
    }
  };

  const handleAddCardSuccess = async (paymentMethod) => {
    try {
      await attachPaymentMethod(service, {
        paymentMethodId: paymentMethod.id,
        setAsDefault: paymentMethod.makeDefault,
      });
      showToast("success", "Card added successfully.");

      if (paymentMethod.makeDefault) {
        setDefaultCardId(paymentMethod.id);
      }

      const newCard = {
        id: paymentMethod.id,
        brand: paymentMethod.card.brand,
        last4: paymentMethod.card.last4,
        exp_month: paymentMethod.card.exp_month,
        exp_year: paymentMethod.card.exp_year,
        isDefault: paymentMethod.makeDefault,
      };

      setCards((prevCards) => {
        if (newCard.isDefault) {
          return prevCards
            .map((card) => ({ ...card, isDefault: false }))
            .concat(newCard);
        }
        return [...prevCards, newCard];
      });

      setAddingCard(false);
    } catch (error) {
      console.error("Failed to add payment method", error);
      showToast("error", error.message || "Failed to add card.");
    }
  };

  const formatCardBrand = (brand) => {
    const brands = {
      visa: "Visa",
      mastercard: "Mastercard",
      amex: "American Express",
      discover: "Discover",
      diners: "Diners Club",
      jcb: "JCB",
      unionpay: "UnionPay",
    };
    return (
      brands[brand.toLowerCase()] ||
      brand.charAt(0).toUpperCase() + brand.slice(1)
    );
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <>
      <Typography variant="h6">Payment Methods</Typography>
      <Divider style={{ margin: "16px 0" }} />

      {cards.length === 0 && !addingCard ? (
        <div style={{ textAlign: "center", padding: 16 }}>
          <CreditCardIcon
            style={{ fontSize: 48, color: "#ccc", marginBottom: 8 }}
          />
          <Typography variant="body1" color="textSecondary">
            No payment methods saved
          </Typography>
        </div>
      ) : (
        cards.map((card) => (
          <MuiCard
            key={card.id}
            variant="outlined"
            style={{ marginBottom: 16 }}
          >
            <CardContent>
              <Grid container alignItems="center" spacing={2}>
                <Grid item>
                  <Avatar>
                    <CreditCardIcon />
                  </Avatar>
                </Grid>
                <Grid item xs>
                  <Typography variant="body1">
                    {formatCardBrand(card.brand)} •••• {card.last4}
                    {card.isDefault && (
                      <span
                        style={{
                          marginLeft: 8,
                          fontSize: "0.75rem",
                          color: "#1976d2",
                          fontWeight: 500,
                        }}
                      >
                        (Default)
                      </span>
                    )}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Expires {String(card.exp_month).padStart(2, "0")}/
                    {String(card.exp_year).slice(-2)}
                  </Typography>
                </Grid>
                <Grid item>
                  <Grid container direction="column" spacing={1}>
                    <Grid item>
                      <Button
                        startIcon={<DeleteIcon />}
                        onClick={() => handleRemoveCard(card.id)}
                        // disabled={
                        //   cards.length === 1 || card.id === defaultCardId
                        // }
                        fullWidth
                        style={{
                          backgroundColor: "#f5f5f5",
                          color: "#000",
                          minWidth: "150px",
                        }}
                      >
                        Remove
                      </Button>
                    </Grid>
                    {card.id !== defaultCardId && (
                      <Grid item>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleSetDefault(card.id)}
                          fullWidth
                          style={{ minWidth: "150px" }}
                        >
                          Set as Default
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </MuiCard>
        ))
      )}

      {addingCard ? (
        <Elements stripe={stripePromise}>
          <AddCardForm
            service={service}
            onCancel={() => setAddingCard(false)}
            onSuccess={handleAddCardSuccess}
            setAsDefault={cards.length === 0}
          />
        </Elements>
      ) : (
        <Button
          variant="contained"
          color="primary"
          onClick={() => setAddingCard(true)}
          style={{ marginTop: 16 }}
        >
          Add Payment Method
        </Button>
      )}
    </>
  );
};
